import { IconButton } from "@package/subframe/components/IconButton";
import { Loader } from "@package/subframe/components/Loader";
import { TextArea } from "@package/subframe/components/TextArea";
import { FeatherArrowLeft } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { CheckSubmitFooter } from "@/components/checks/CheckSubmitFooter";
import type { PassFailValue } from "@/components/checks/PassFailControl";
import { PassFailControl } from "@/components/checks/PassFailControl";
import { FeatureGate } from "@/components/shared/FeatureGate";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { getListEmergencyLightChecksQueryControllerListQueryKey } from "@/generated/emergency-light-checks/emergency-light-checks";
import {
  getListEmergencyLightsQueryControllerListQueryKey,
  useListEmergencyLightsQueryControllerList,
} from "@/generated/emergency-lights/emergency-lights";
import { useOfflineEmergencyLightCheck } from "@/hooks/use-offline-emergency-light-check";
import { useOfflineRegister } from "@/hooks/use-offline-register";
import {
  EMERGENCY_LIGHT_CHECK_COPY,
  type EmergencyLightCheckKind,
  groupLightsByRoom,
  toEmergencyLightCheckKind,
} from "@/lib/emergency-light-checks";
import { FEATURES } from "@/lib/features";
import { canRaiseFollowUp } from "@/lib/roles";

export const Route = createFileRoute(
  "/_authenticated/emergency-light-checks/new",
)({
  validateSearch: (
    search: Record<string, unknown>,
  ): { kind: EmergencyLightCheckKind } => ({
    kind: toEmergencyLightCheckKind(search.kind),
  }),
  component: NewEmergencyLightCheckPageRoute,
});

interface ItemState {
  result: PassFailValue;
  comment: string;
}

function NewEmergencyLightCheckPageRoute() {
  return (
    <FeatureGate feature={FEATURES.MONITORING} featureName="Monitoring">
      <NewEmergencyLightCheckPage />
    </FeatureGate>
  );
}

function NewEmergencyLightCheckPage() {
  const { kind } = Route.useSearch();
  const copy = EMERGENCY_LIGHT_CHECK_COPY[kind];
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isPlatformAdmin, userRoleType } = useAuth();
  const { selectedNurseryId, nurseryRole } = useNursery();
  // Only managers and group admins may create follow-ups, so everyone else
  // gets plain Submit rather than a button that saves and then 403s.
  const canRaise = canRaiseFollowUp({
    isPlatformAdmin,
    userRoleType,
    nurseryRole,
  });

  const lightsQuery = useListEmergencyLightsQueryControllerList(
    selectedNurseryId ?? "",
    {},
    // Always the current register: AC13 is judged against it, so a cached list
    // missing a newly registered light would only fail at submit.
    { query: { enabled: !!selectedNurseryId, refetchOnMount: "always" } },
  );
  const {
    items: lights,
    isLoading,
    isServedFromCache,
  } = useOfflineRegister("emergency-lights", selectedNurseryId, lightsQuery);

  const [state, setState] = useState<Record<string, ItemState>>({});
  const itemFor = (id: string): ItemState =>
    state[id] ?? { result: undefined, comment: "" };

  const setResult = (id: string, result: Exclude<PassFailValue, undefined>) =>
    setState((prev) => ({ ...prev, [id]: { ...itemFor(id), result } }));
  const setComment = (id: string, comment: string) =>
    setState((prev) => ({ ...prev, [id]: { ...itemFor(id), comment } }));

  const byRoom = useMemo(() => groupLightsByRoom(lights), [lights]);

  // AC13: no light may be skipped. The server rejects a partial payload, but a
  // form that defaulted to "pass" would send a complete — and untrue — one, so
  // submission is blocked until every light has been answered explicitly.
  const unanswered = lights.filter(
    (light) => itemFor(light.id).result === undefined,
  );
  const allAnswered = lights.length > 0 && unanswered.length === 0;

  const goBack = () =>
    navigate({ to: "/emergency-light-checks", search: { kind } });

  const { submit, isPending, syncStatus } = useOfflineEmergencyLightCheck();
  // Offline and never loaded on this device: "none registered" would be untrue.
  const isRegisterUnavailable =
    !syncStatus.isOnline && !lightsQuery.data && !isServedFromCache;
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // hasSubmitted keeps the form locked between the write and the navigation so
  // a second tap can't record the inspection twice.
  const isSubmitting = isPending || hasSubmitted;

  const failedLightIds = lights
    .filter((light) => itemFor(light.id).result === "fail")
    .map((light) => light.id);

  const handleSubmit = async (raiseFollowUp = false) => {
    if (isSubmitting || !selectedNurseryId || !allAnswered) return;
    setHasSubmitted(true);

    let outcome: Awaited<ReturnType<typeof submit>>["outcome"];
    let created: Awaited<ReturnType<typeof submit>>["created"];
    let rejection: string | undefined;
    try {
      ({
        outcome,
        created,
        error: rejection,
      } = await submit({
        nurseryId: selectedNurseryId,
        kind,
        items: lights.map((light) => {
          const item = itemFor(light.id);
          return {
            emergencyLightId: light.id,
            passed: item.result === "pass",
            comment: item.comment.trim() || undefined,
          };
        }),
      }));
    } catch (error) {
      // requireCachedUserId throws when no signed-in user is cached, which
      // would otherwise reject unhandled and leave the form locked.
      setHasSubmitted(false);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to submit check — please try again",
      );
      return;
    }

    if (outcome === "failed") {
      // The queue swallows HTTP failures, so without this the form would clear
      // and the manager would believe a statutory record had been filed.
      setHasSubmitted(false);
      toast.error(rejection ?? "Failed to submit check — please try again");
      // The usual cause is a register that changed since the form loaded;
      // refetch so the corrected retry lists the right lights.
      void queryClient.invalidateQueries({
        queryKey:
          getListEmergencyLightsQueryControllerListQueryKey(selectedNurseryId),
      });
      return;
    }

    toast.success(
      outcome === "queued"
        ? `${copy.title} saved — it will sync when you're back online`
        : `${copy.title} submitted`,
    );

    // Await the refetch before navigating: mounting the history page mid-fetch
    // lets observer churn cancel it and leave the stale list.
    await queryClient.invalidateQueries({
      queryKey:
        getListEmergencyLightChecksQueryControllerListQueryKey(
          selectedNurseryId,
        ),
      refetchType: "all",
    });

    // AC17. A queued check has no server ids yet, so there is no item to attach
    // a follow-up to — land on the history instead of a form that would 400.
    if (raiseFollowUp && outcome === "synced" && created) {
      const failed = created.items.filter((item) => !item.passed);
      const only = failed.length === 1 ? failed[0] : undefined;
      if (only) {
        const light = lights.find((l) => l.id === only.emergencyLightId);
        navigate({
          to: "/follow-up-actions/create",
          search: {
            nurseryId: selectedNurseryId,
            scheduledQuestionnaireId: "",
            answerId: "",
            questionText: "",
            checklistName: "",
            emergencyLightCheckId: created.id,
            emergencyLightCheckItemId: only.id,
            emergencyLightName: light?.name,
            fromSubmission: true,
          },
          // Replace the submitted form, so going back cannot reopen it blank.
          replace: true,
        });
        return;
      }
      // Zero or several failures: there is no single unit to bind to, so open
      // the check where every failed light offers its own Raise follow-up.
      navigate({
        to: "/emergency-light-checks/$checkId",
        params: { checkId: created.id },
        replace: true,
      });
      return;
    }

    if (raiseFollowUp && outcome === "queued") {
      toast.info(
        "Raise the follow-up from the check once it has synced — it has no server record yet.",
      );
    }

    // Replace the submitted form, so going back cannot reopen it blank.
    navigate({
      to: "/emergency-light-checks",
      search: { kind },
      replace: true,
    });
  };

  return (
    <div className="flex w-full max-w-2xl flex-col items-start gap-6 bg-white animate-fade-in">
      <div className="flex w-full items-start gap-2">
        <IconButton
          aria-label="Back"
          icon={<FeatherArrowLeft />}
          onClick={goBack}
        />
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
          <span className="w-full text-metrics font-metrics text-primary-navy-800">
            {copy.title}
          </span>
          <span className="text-body font-body text-subtext-color">
            {copy.subtitle}
          </span>
        </div>
      </div>

      {!syncStatus.isOnline && (
        <div className="flex w-full items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <span className="text-body font-body text-default-font">
            You&apos;re offline. This check will sync when you reconnect.
          </span>
        </div>
      )}

      <ol className="flex w-full list-decimal flex-col gap-2 rounded-lg border border-neutral-200 bg-neutral-50 py-4 pl-9 pr-4">
        {copy.instructions.map((step) => (
          <li key={step} className="text-body font-body text-default-font">
            {step}
          </li>
        ))}
      </ol>

      {isLoading ? (
        <div className="flex w-full items-center justify-center py-12">
          <Loader size="large" />
        </div>
      ) : lights.length === 0 ? (
        <div className="flex w-full flex-col items-start gap-2 rounded-md border border-neutral-200 p-6">
          <span className="text-body font-body text-default-font">
            {isRegisterUnavailable ? (
              "Connect once to load the register."
            ) : (
              <>
                No emergency lights are registered for this nursery yet. Ask a
                group admin to add them in the nursery&apos;s settings.
              </>
            )}
          </span>
        </div>
      ) : (
        <>
          <div className="flex w-full flex-col items-start gap-6">
            {byRoom.map((group) => (
              <div
                key={group.key}
                className="flex w-full flex-col items-start gap-3"
              >
                <span className="text-heading-3 font-heading-3 text-default-font">
                  {group.label}
                </span>
                {group.lights.map((light) => {
                  const item = itemFor(light.id);
                  return (
                    <div
                      key={light.id}
                      className={`flex w-full flex-col items-start gap-3 rounded-lg border p-4 ${
                        item.result === "fail"
                          ? "border-red-200 bg-red-50"
                          : "border-neutral-200"
                      }`}
                    >
                      <div className="flex w-full flex-wrap items-center justify-between gap-3">
                        <span className="text-body-bold font-body-bold text-default-font">
                          {light.name}
                        </span>
                        <PassFailControl
                          label={light.name}
                          value={item.result}
                          onChange={(result) => setResult(light.id, result)}
                          disabled={isSubmitting}
                        />
                      </div>
                      <TextArea
                        className="h-auto w-full"
                        label="Comment (optional)"
                      >
                        <TextArea.Input
                          placeholder="Note anything that needs attention"
                          value={item.comment}
                          onChange={(e) => setComment(light.id, e.target.value)}
                          disabled={isSubmitting}
                          maxLength={4000}
                        />
                      </TextArea>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <CheckSubmitFooter
            onCancel={goBack}
            onSubmit={() => void handleSubmit(false)}
            onSubmitAndRaiseFollowUp={
              canRaise ? () => void handleSubmit(true) : undefined
            }
            isSubmitting={isSubmitting}
            canSubmit={allAnswered}
            hasFailure={failedLightIds.length > 0}
            hint={
              allAnswered
                ? undefined
                : `${unanswered.length} light${unanswered.length === 1 ? "" : "s"} still to record`
            }
          />
        </>
      )}
    </div>
  );
}
